import { Component, HostBinding, Optional, Self, OnDestroy, Input, OnInit, SimpleChanges, OnChanges, ChangeDetectorRef } from '@angular/core';
import {
    FormGroup,
    NgControl,
    ControlValueAccessor,
    ReactiveFormsModule,
    FormControl
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ErrorStateMatcher, MatNativeDateModule } from '@angular/material/core';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { DatePickerErrorStateMatcher } from '../date-picker/date-picker.component';
import { CommonModule } from '@angular/common';
import { BaseChangeEvent, RangePicker } from '@nagel-types';
import { getFormattedTimezoneAdjustedDate } from '@nagel-utils';
import { DateRangeValidators } from '@nagel-validators';
import {
    FILTER_START_DATE_RANGE_KEY,
    FILTER_END_DATE_RANGE_KEY,
    INVALID_DATE_RANGE_ERROR,
    DATE_RANGE_LIMIT_ERROR
} from '@nagel-consts';

const DEBOUNCE_RANGE_PICKER_TIME_IN_MS = 1000;

@Component({
    selector: 'lib-range-picker',
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        CommonModule
    ],
    templateUrl: './range-picker.component.html',
    styleUrl: './range-picker.component.scss'
})
export class RangePickerFieldComponent implements ControlValueAccessor, OnDestroy, OnInit, OnChanges {
    @Input() fieldKey?: string = '';
    @Input() className?: string = '';
    @Input() defaultValues?: RangePicker;
    @Input() onChange?: (event: BaseChangeEvent) => void;
    @Input() setHasError: (hasError: boolean) => void = () => {};
    dateRangeForm!: FormGroup;
    stateChanges = new Subject<void>();
    focused = false;
    required = false;
    private _placeholder = '';
    static nextId = 0;
    matcher: ErrorStateMatcher = new DatePickerErrorStateMatcher();

    @HostBinding() id = `mat-range-${RangePickerFieldComponent.nextId++}`;

    constructor(@Optional() @Self() public ngControl: NgControl, private cdr: ChangeDetectorRef) {
        this.dateRangeForm = new FormGroup({
            startDate: new FormControl(''),
            endDate: new FormControl('')
        }, { validators: DateRangeValidators.isDateRangeValidValidator(FILTER_START_DATE_RANGE_KEY, FILTER_END_DATE_RANGE_KEY) });

        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit() {
        if (this.ngControl != null) {
            this.ngControl.control?.valueChanges
                .pipe(debounceTime(DEBOUNCE_RANGE_PICKER_TIME_IN_MS))
                .subscribe(this.handleValueUpdate);
        }

        this.dateRangeForm.patchValue({
            startDate: this.defaultValues?.startDate || '',
            endDate: this.defaultValues?.endDate || ''
        });

        if (this.ngControl && this.ngControl.disabled) {
            this.dateRangeForm.disable();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['defaultValues']) {
            this.value = this.defaultValues || { startDate: '', endDate: '' };
        }
    }

    @Input()
    set value(value: RangePicker) {
        this.dateRangeForm.patchValue(value);
        this.stateChanges.next();
    }

    get value() {
        return this.dateRangeForm.value;
    }

    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    get empty() {
        const formValue = this.dateRangeForm.value;
        return !(formValue.startDate && formValue.endDate);
    }

    get placeholder(): string {
        return this._placeholder;
    }

    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    get errorState(): boolean {
        return this.dateRangeForm.invalid;
    }

    onTouched = () => {};

    onChangeSubs: Subscription[] = [];

    ngOnDestroy() {
        this.stateChanges.complete();
        this.onChangeSubs.forEach(sub => sub.unsubscribe());
    }

    writeValue(value: RangePicker): void {
        if (value) {
            this.dateRangeForm.setValue(value, { emitEvent: false });
        }
        this.stateChanges.next();
    }

    registerOnChange(onChange: () => void): void {
        const subscription = this.dateRangeForm.valueChanges.subscribe(onChange);
        this.onChangeSubs.push(subscription);
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    getIsDisabled(): boolean {
        return this.ngControl?.disabled || false;
    }

    private handleValueUpdate = (value: {startDate: string | Date, endDate: string | Date}) => {
        if (value) {
            let { startDate: start, endDate: end } = value;
            start = start || '';
            end = end || '';

            if (start instanceof Date) {
                start = getFormattedTimezoneAdjustedDate(start);
            }

            if (end instanceof Date) {
                end = getFormattedTimezoneAdjustedDate(end);
            }

            const shouldFormat = start !== value.startDate || end !== value.endDate;
            if (shouldFormat) {
                this.dateRangeForm.setValue({startDate: start, endDate: end}, { emitEvent: true });
                this.ngControl.viewToModelUpdate({startDate: start, endDate: end});
            }

            this.ngControl.control?.setErrors(DateRangeValidators.isDateRangeValidValidator(FILTER_START_DATE_RANGE_KEY, FILTER_END_DATE_RANGE_KEY)(this.dateRangeForm));
        }
    };

    handleOnChange = () => {
        if (this.onChange) {
            const outputValue = {
                value: this.dateRangeForm.value,
                key: this.fieldKey
            };
            this.onChange(outputValue as BaseChangeEvent);
        }

        this.setHasError(this.dateRangeForm.hasError(INVALID_DATE_RANGE_ERROR) || this.dateRangeForm.hasError(DATE_RANGE_LIMIT_ERROR));
    };

    getErrorMessage = () => {
        let message = '';
        if (this.dateRangeForm.hasError(INVALID_DATE_RANGE_ERROR)) {
            message = $localize`Start date should be before end date.`;
        }

        if (this.dateRangeForm.hasError(DATE_RANGE_LIMIT_ERROR)) {
            message = $localize`Date range exceeds 5 days`;
        }

        return message;
    };
}